import React from 'react'
import { connect } from 'react-redux'
import { Button, Card, Table, Tag, message } from 'antd'
import dayjs from 'dayjs'
import PageHeader from '@/components/PageHeader'
import Permission from '@/components/PermissionA5'
import { DatePickerC, SelectC } from '@/i2antdV5'
import { formatDayString, parseHmStringToDayMinutes, renderTextStr } from '@/utils/renderUtils'
import { fetchJStoreList } from '@/redux/components/getJStoreListSlice'
import { fetchTuinaReservationList } from '@/redux/tuina/reservationListSlice'
import { fetchTuinaTherapistOnDuty } from '@/redux/tuina/therapistOnDutySlice'
import { fetchTuinaMemberList } from '@/redux/tuina/memberListSlice'
import { TUINA_RESERVATION_STATUS_OPTIONS } from '@/views/tuina/options'
import ReservationRescheduleModal from '@/views/tuina/board/share/ReservationRescheduleModal'
import ReservationCancelModal from '@/views/tuina/board/share/ReservationCancelModal'
import './Page.less'

const breadcrumbList = [
  { breadcrumbName: '按摩' },
  { breadcrumbName: '预约列表' }
]

/** 列表默认查询（仅页面侧使用，不写入 Redux initialState） */
const DEFAULT_TUINA_RESERVATION_LIST_QUERY = {
  pageNumber: 1,
  pageSize: 20,
  appointmentDate: dayjs().startOf('day').valueOf()
}

const mergeTuinaReservationListQuery = (reduxQuery) =>
  reduxQuery && Object.keys(reduxQuery).length > 0 ? reduxQuery : DEFAULT_TUINA_RESERVATION_LIST_QUERY

function formatReservationClock (raw) {
  if (!raw) return ''
  const minutes = parseHmStringToDayMinutes(raw)
  if (minutes == null) return ''
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

class TuinaAppointmentsPage extends React.Component {
  didInitList = false

  componentDidMount () {
    this.props.fetchJStoreList({ query: {}, dataType: 'optionTuina' })
      .unwrap()
      .catch((err) => message.error(typeof err === 'string' ? err : '加载门店列表失败'))
  }

  componentDidUpdate (prevProps) {
    if (this.didInitList) return
    if (this.props.getJStoreList.fetching) return
    if (!prevProps.getJStoreList.fetching) return
    const options = this.props.getJStoreList.data
    const first = Array.isArray(options) && options[0]
    const query = { ...DEFAULT_TUINA_RESERVATION_LIST_QUERY }
    if (first && first.id != null && first.id !== '') query.storeId = first.id
    this.fetchFilterOptions(query)
    this.fetchList(query)
    this.didInitList = true
  }

  fetchFilterOptions = (formatQuery = {}) => {
    const storeId = formatQuery.storeId
    const therapistQuery = {}
    const memberQuery = { pageNumber: 1, pageSize: 200 }
    if (storeId != null && storeId !== '') {
      therapistQuery.storeId = storeId
      memberQuery.storeId = storeId
    }
    this.props
      .fetchTuinaTherapistOnDuty({ query: therapistQuery, dataType: 'option' })
      .unwrap()
      .catch((err) => message.error(typeof err === 'string' ? err : '加载师傅失败'))
    this.props
      .fetchTuinaMemberList({ query: memberQuery, dataType: 'option' })
      .unwrap()
      .catch((err) => message.error(typeof err === 'string' ? err : '加载会员失败'))
  }

  fetchList = (formatQuery) => {
    if (this.didInitList) {
      const prevStoreId = this.props.reservationList.query.storeId
      if (String(prevStoreId ?? '') !== String(formatQuery.storeId ?? '')) {
        this.fetchFilterOptions(formatQuery)
      }
    }
    this.props
      .fetchTuinaReservationList({ query: formatQuery })
      .unwrap()
      .catch((err) => message.error(typeof err === 'string' ? err : '加载预约失败'))
  }

  handleTableChange = (pagination) => {
    const query = mergeTuinaReservationListQuery(this.props.reservationList.query)
    this.fetchList({
      ...query,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    })
  }

  handleModalSuccess = () => {
    this.fetchList(mergeTuinaReservationListQuery(this.props.reservationList.query))
  }

  getColumns = () => [
    {
      title: '预约号',
      dataIndex: 'reservationNo',
      width: 140,
      render: (v) => renderTextStr(v)
    },
    {
      title: '会员',
      dataIndex: 'memberName',
      width: 120,
      render: (v) => renderTextStr(v)
    },
    {
      title: '日期',
      dataIndex: 'appointmentDate',
      width: 120,
      render: (v) => renderTextStr(formatDayString(v))
    },
    {
      title: '时间',
      key: 'time',
      width: 140,
      render: (_, record) => {
        const start = formatReservationClock(record.startTime)
        const end = formatReservationClock(record.endTime)
        if (!start && !end) return renderTextStr('')
        if (!end) return renderTextStr(start)
        return `${start} 至 ${end}`
      }
    },
    {
      title: '项目',
      dataIndex: 'projectName',
      render: (v) => renderTextStr(v)
    },
    {
      title: '时长',
      dataIndex: 'durationMinutes',
      width: 88,
      render: (v) => {
        const n = Number(v)
        if (v == null || v === '' || !Number.isFinite(n)) return renderTextStr('')
        return <span className="tuina-appt-meta">{n}分钟</span>
      }
    },
    {
      title: '师傅',
      dataIndex: 'therapistName',
      width: 100,
      render: (v) => (v ? renderTextStr(v) : '待派')
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (v) => {
        const row = TUINA_RESERVATION_STATUS_OPTIONS.find((item) => item.id === v)
        const text = (row && row.value) || v
        if (!text) return renderTextStr(text)
        return <Tag color={(row && row.color) || 'default'}>{text}</Tag>
      }
    },
    {
      title: '备注',
      dataIndex: 'remark',
      ellipsis: true,
      render: (v) => renderTextStr(v)
    },
    {
      title: '取消原因',
      dataIndex: 'cancelReason',
      ellipsis: true,
      render: (v) => renderTextStr(v)
    },
    {
      title: '操作',
      key: 'actions',
      width: 148,
      render: (_, record) => {
        if (record.status !== 'booked') return null
        return (
          <Permission rootPath="tuinaPage">
            <div pathitem={['appointments']} checkarray={['buttonCodes']} className="tuina-appt-actions">
              <ReservationRescheduleModal record={record} onSuccess={this.handleModalSuccess}>
                <Button type="link" aria-label={`改 ${record.reservationNo}`}>修改</Button>
              </ReservationRescheduleModal>
              <ReservationCancelModal record={record} onSuccess={this.handleModalSuccess}>
                <Button type="link" danger aria-label={`取消 ${record.reservationNo}`}>取消</Button>
              </ReservationCancelModal>
            </div>
          </Permission>
        )
      }
    }
  ]

  render () {
    const { reservationList, getJStoreList, therapistOnDuty, memberList } = this.props
    const { fetching, query: reduxQuery, data } = reservationList
    const query = mergeTuinaReservationListQuery(reduxQuery)
    const list = Array.isArray(data.list) ? data.list : []
    const totalRow = data.totalRow || 0
    const jStoreOptions = getJStoreList.data
    const therapistOptions = Array.isArray(therapistOnDuty.data) ? therapistOnDuty.data : []
    const memberOptions = Array.isArray(memberList.data) ? memberList.data : []

    return (
      <div>
        <PageHeader title="预约列表" breadcrumbList={breadcrumbList} />
        <div className="i2-container">
          <Card className="i2-card-page">
            <section className="i2-app-toolbar">
              <div className="toolbar-filters">
                <SelectC
                  label="门店"
                  query={query}
                  searchKey="storeId"
                  options={jStoreOptions}
                  action={this.fetchList}
                  placeholder="全部"
                  clearKey="therapistId,memberId"
                  modalProps={{ allowClear: true }}
                />
                <SelectC
                  label="师傅"
                  query={query}
                  searchKey="therapistId"
                  options={therapistOptions}
                  action={this.fetchList}
                  placeholder="全部"
                  modalProps={{ allowClear: true }}
                />
                <SelectC
                  label="会员"
                  query={query}
                  searchKey="memberId"
                  options={memberOptions}
                  action={this.fetchList}
                  placeholder="全部"
                  modalProps={{ allowClear: true }}
                />
                <DatePickerC
                  label="日期"
                  query={query}
                  searchKey="appointmentDate"
                  action={this.fetchList}
                  modalProps={{ allowClear: true }}
                />
                <SelectC
                  label="状态"
                  query={query}
                  searchKey="status"
                  options={TUINA_RESERVATION_STATUS_OPTIONS}
                  action={this.fetchList}
                  placeholder="全部"
                  modalProps={{ allowClear: true }}
                />
              </div>
            </section>
            <Table
              className="tuina-appt-table"
              rowKey={(r) => String(r.id)}
              columns={this.getColumns()}
              dataSource={list}
              loading={fetching}
              onChange={this.handleTableChange}
              pagination={{
                current: query.pageNumber,
                pageSize: query.pageSize,
                total: totalRow,
                showSizeChanger: true,
                showTotal: (t) => `共 ${t} 条`,
                pageSizeOptions: ['10', '20', '50', '100']
              }}
            />
          </Card>
        </div>
      </div>
    )
  }
}

const mapStateToProps = (state) => ({
  reservationList: state.tuina.reservationList,
  getJStoreList: state.components.getJStoreList,
  therapistOnDuty: state.tuina.therapistOnDuty,
  memberList: state.tuina.memberList
})

const mapDispatchToProps = {
  fetchTuinaReservationList,
  fetchJStoreList,
  fetchTuinaTherapistOnDuty,
  fetchTuinaMemberList
}

export default connect(mapStateToProps, mapDispatchToProps)(TuinaAppointmentsPage)
